import axios from 'axios';

export class SessionExpiredError extends Error {
  constructor() {
    super('Your session has expired. Please sign in again to manage billing.');
    this.isSessionExpired = true;
  }
}

// Use the same session as dashboard /auth/login. The old `tokens` cache may
// belong to an earlier login and is deliberately never used as a fallback.
export const getAccessToken = () => {
  const token = localStorage.getItem('accessToken');
  if (!token || token === 'null' || token === 'undefined') {
    throw new SessionExpiredError();
  }
  return token;
};

let refreshInFlight;
let lastRefresh;

export const refreshDashboardSession = rejectedToken => {
  const currentToken = localStorage.getItem('accessToken');
  // A concurrent request may already have completed refresh.
  if (currentToken !== rejectedToken) {
    if (lastRefresh?.previousAccessToken === rejectedToken &&
        lastRefresh.accessToken === currentToken &&
        lastRefresh.refreshToken === localStorage.getItem('refreshToken')) {
      return Promise.resolve(currentToken);
    }
    return Promise.reject(new SessionExpiredError());
  }
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken || refreshToken === 'null' || refreshToken === 'undefined') {
    return Promise.reject(new SessionExpiredError());
  }

  // Refresh tokens rotate. Concurrent billing requests must share one refresh.
  refreshInFlight = (async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/refresh`,
        { refreshToken }
      );
      const tokens = response?.data;
      if (!tokens?.accessToken || !tokens?.refreshToken) {
        throw new SessionExpiredError();
      }
      // Never restore a session after sign-out or overwrite a new login.
      if (localStorage.getItem('refreshToken') !== refreshToken ||
          localStorage.getItem('accessToken') !== rejectedToken) {
        throw new SessionExpiredError();
      }
      lastRefresh = { previousAccessToken: rejectedToken, ...tokens };
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      return tokens.accessToken;
    } catch (error) {
      if ([401, 403].includes(error.response?.status)) {
        throw new SessionExpiredError();
      }
      throw error;
    } finally {
      refreshInFlight = undefined;
    }
  })();
  return refreshInFlight;
};
